import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useFunnelStore } from '../../store/funnelStore';
import { SectionType } from '../../types/section.types';
import { createDefaultSection } from '../../utils/helpers';

interface AddSectionModalProps {
  onClose: () => void;
}

/**
 * Add Section Modal Component
 * Modal dialog for adding a new section to the funnel
 */
const AddSectionModal: React.FC<AddSectionModalProps> = ({ onClose }) => {
  const { addSection } = useFunnelStore();
  const [selectedType, setSelectedType] = useState<SectionType>('content');
  const [sectionName, setSectionName] = useState('');

  const sectionTypes: Array<{ type: SectionType; label: string; description: string }> = [
    { type: 'header', label: 'Header', description: 'Top navigation with logo and menu' },
    { type: 'hero', label: 'Hero', description: 'Large header section with CTA' },
    { type: 'content', label: 'Content', description: 'General content section' },
    { type: 'features', label: 'Features', description: 'Showcase product features' },
    { type: 'testimonials', label: 'Testimonials', description: 'Customer reviews and quotes' },
    { type: 'pricing', label: 'Pricing', description: 'Pricing tables and plans' },
    { type: 'faq', label: 'FAQ', description: 'Frequently asked questions' },
    { type: 'cta', label: 'Call to Action', description: 'Conversion-focused section' },
    { type: 'footer', label: 'Footer', description: 'Bottom page information' },
    { type: 'custom', label: 'Custom', description: 'Blank custom section' },
  ];

  const handleAdd = () => {
    const section = createDefaultSection(selectedType);
    if (sectionName.trim()) {
      section.name = sectionName.trim();
    }
    addSection(section);
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Add New Section</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 max-h-96 overflow-y-auto">
          {/* Section Name Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Section Name (Optional)
            </label>
            <input
              type="text"
              value={sectionName}
              onChange={(e) => setSectionName(e.target.value)}
              placeholder="e.g., Hero Section"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Section Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Section Type
            </label>
            <div className="space-y-2">
              {sectionTypes.map((type) => (
                <label
                  key={type.type}
                  className={`
                    flex items-start p-3 border rounded-md cursor-pointer transition-all
                    ${
                      selectedType === type.type
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="sectionType"
                    value={type.type}
                    checked={selectedType === type.type}
                    onChange={() => setSelectedType(type.type)}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{type.label}</div>
                    <div className="text-sm text-gray-500">{type.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
          >
            Add Section
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddSectionModal;

